/**
 * The vertical lifecycle rail on the Contest Overview's Schedule card —
 * Created · Published · Opened · Closed · Settled, one hairline-separated row
 * each, with its own date on the right (CAE-1546).
 *
 * Spec: `.schedule-list` / `.schedule-step*` in
 * `prototype/contest-overview.css`, and the `Schedule` component in the
 * handoff's `app.jsx`.
 *
 * > **This is the orientation the handoff's prototype draws, and it is the one
 * > that ships.** The written `02-contest-overview/README.md` and the four
 * > reference screenshots describe a *horizontal* four-dot track with a
 * > separate date row — which is what `MilestoneTrack` implements and what this
 * > card used first. The prototype's own JSX and stylesheet have since replaced
 * > it with this five-row list, adding the **Published** step the four-dot
 * > version has nowhere to put. Taken as vertical on review of CAE-1546, which
 * > also matches the sibling Over/Under admin's shipped `LifecycleRail`.
 * > Recorded in docs/design/admin-ui/prototype/README.md.
 *
 * Rows are separated by their own bottom hairline rather than joined by a
 * connector line, as the stylesheet has it: a rail drawn behind the discs would
 * have to assume every row is the same height, and a label or date that wraps
 * breaks that assumption silently.
 */
import Box from '@mui/material/Box';
import type { Theme } from '@mui/material/styles';

/** Where a milestone sits relative to the contest's progress. */
export type MilestoneState = 'done' | 'current' | 'future';

export interface RailMilestone {
  /** The step's name, as the rail prints it — "Created", "Published", … */
  label: string;
  state: MilestoneState;
  /**
   * The moment itself, pre-formatted, or `null` where there is none.
   *
   * Formatted by the caller because the rail has no opinion about dates; it
   * only decides that an absent one is drawn quietly rather than skipped.
   */
  date: string | null;
  /**
   * What an undated step says instead of an em-dash.
   *
   * The prototype's Published row reads "Awaiting admin action" rather than
   * "—": the step has no date because nobody has taken it yet, which is a
   * different silence from a step that simply has no timestamp to show.
   */
  pendingNote?: string;
}

const DISC = 22;

const Tick = () => (
  <Box
    component="svg"
    aria-hidden
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    sx={{ display: 'block' }}
  >
    <path
      d="M5 12.5l4.5 4.5L19 7.5"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Box>
);

interface MilestoneRailProps {
  /** Names the rail for assistive tech, e.g. "Contest lifecycle". */
  label: string;
  /**
   * `readonly` so a helper's own `readonly RailMilestone[]` spreads straight in
   * — `scheduleRail(contest)` returns one, and a mutable prop would reject it.
   */
  milestones: readonly RailMilestone[];
}

export const MilestoneRail = ({ label, milestones }: MilestoneRailProps) => (
  <Box
    component="ol"
    aria-label={label}
    sx={{
      listStyle: 'none',
      margin: 0,
      padding: '6px 0 0',
      display: 'flex',
      flexDirection: 'column',
    }}
  >
    {milestones.map((milestone) => {
      const { label: step, state, date, pendingNote } = milestone;

      return (
        <Box
          key={step}
          component="li"
          data-testid={`schedule-step-${step.toLowerCase()}`}
          data-state={state}
          // The discs are `aria-hidden` and the done/current distinction is
          // carried by colour alone, so without this the rail reads as five
          // undifferentiated rows. `step` is the token for "the current one in
          // a sequence".
          aria-current={state === 'current' ? 'step' : undefined}
          sx={(theme: Theme) => ({
            display: 'grid',
            gridTemplateColumns: `24px minmax(0, 1fr) auto`,
            gap: '12px',
            alignItems: 'center',
            padding: '11px 0',
            borderBottom: theme.tokens.border.default,
            '&:last-of-type': { borderBottom: 0 },
          })}
        >
          <Box
            aria-hidden
            component="span"
            sx={(theme: Theme) => ({
              width: `${DISC}px`,
              height: `${DISC}px`,
              borderRadius: '50%',
              display: 'grid',
              placeItems: 'center',
              flexShrink: 0,
              transition: 'background 0.15s, color 0.15s, border-color 0.15s',
              ...(state === 'done' && {
                backgroundColor: theme.tokens.color.successSoft,
                color: theme.tokens.color.success,
                border: `${theme.tokens.border.width} solid ${theme.tokens.componentColor.milestoneDoneBorder}`,
              }),
              ...(state === 'current' && {
                backgroundColor: theme.tokens.color.accent,
                color: theme.tokens.color.surface,
                border: `${theme.tokens.border.width} solid ${theme.tokens.color.accent}`,
                /*
                 * The variable, never the keyframe name — the theme layer's own
                 * contract (`theme/README.md`). `@keyframes` are global to the
                 * document and the admin loads Tailwind, so the name is
                 * namespaced in `css-variables.ts` and published as this
                 * variable; `:root` sets it to `none` under
                 * `prefers-reduced-motion: reduce`, which is how all three of
                 * the admin's perpetual animations stop at once. A ring that
                 * breathes forever is decoration, and the step still wears the
                 * accent fill that marks it current.
                 */
                animation: 'var(--animation-step-pulse)',
              }),
              ...(state === 'future' && {
                backgroundColor: theme.tokens.color.surface,
                // Dashed, and 1.5px rather than the hairline the other two
                // wear: a step nobody has reached is drawn as an outline, and
                // at 22px a 0.5px dash reads as a solid ring.
                border: `1.5px dashed ${theme.tokens.componentColor.toggleTrackOff}`,
              }),
            })}
          >
            {state === 'done' ? <Tick /> : null}
            {state === 'current' ? (
              <Box
                component="span"
                sx={(theme: Theme) => ({
                  width: '7px',
                  height: '7px',
                  borderRadius: '50%',
                  backgroundColor: theme.tokens.color.surface,
                })}
              />
            ) : null}
          </Box>

          <Box
            component="span"
            sx={(theme: Theme) => ({
              minWidth: 0,
              fontSize: '13.5px',
              fontWeight: state === 'current' ? 600 : 500,
              letterSpacing: '-0.005em',
              color:
                state === 'current'
                  ? theme.tokens.color.ink
                  : state === 'future'
                    ? theme.tokens.color.ink3
                    : theme.tokens.color.ink2,
            })}
          >
            {step}
          </Box>

          <Box
            component="span"
            sx={(theme: Theme) => ({
              display: 'inline-flex',
              alignItems: 'baseline',
              gap: '6px',
              fontFamily: theme.tokens.font.mono,
              fontVariantNumeric: 'tabular-nums',
              fontSize: '12.5px',
              letterSpacing: '-0.01em',
              // `white-space: nowrap`, per `.schedule-step-when`. The date owns
              // its own row here, so the compact `28 May · 13:17` form never
              // has to wrap the way it did in the four-column grid this rail
              // replaced.
              whiteSpace: 'nowrap',
              ...(date
                ? {
                    color: state === 'future' ? theme.tokens.color.ink3 : theme.tokens.color.ink2,
                    fontWeight: state === 'future' ? 400 : 500,
                  }
                : { color: theme.tokens.color.ink4 }),
            })}
          >
            {date ?? pendingNote ?? '—'}
          </Box>
        </Box>
      );
    })}
  </Box>
);
